import type { SyntheticEvent } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { trackRequestSent } from '@/analytics/events';
import { TextInput } from '@/components/common/TextInput';
import { Layout } from '@/components/Layout';
import { useCreateRequest } from '@/hooks/useCreateRequest';

type Mode = 'link' | 'name';

// Link-first request (RFC 016): the user pastes a link — a coffee's product
// page or a roaster's shop — and we capture it as-is (never fetched or
// parsed). A name is the slower fallback. Fulfilment happens off-app through
// the crawl pipeline, so there is nothing else to fill in here.
export function RequestPage() {
  const [params] = useSearchParams();
  const { createRequest, isSubmitting, error } = useCreateRequest();

  const [mode, setMode] = useState<Mode>('link');
  const [link, setLink] = useState('');
  // Seed the fallback with the search term the user came from, so switching to
  // it keeps their words even though the link is offered first.
  const [name, setName] = useState(params.get('name') ?? '');
  const [sent, setSent] = useState(false);

  const value = (mode === 'link' ? link : name).trim();
  const canSubmit = value.length > 0 && !isSubmitting;

  const handleSubmit = async (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (!canSubmit) return;
    const input = mode === 'link' ? { url: value } : { name: value };
    const created = await createRequest(input);
    if (created) {
      trackRequestSent({ has_link: mode === 'link' });
      setSent(true);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-4 py-8">
        <article className="rounded-xl bg-wolisho-card p-4 md:p-6">
          {sent ? (
            <div className="py-6 text-center" data-testid="request-sent">
              <h1 className="mt-2 text-2xl font-bold text-black">Request sent</h1>
              <p className="mx-auto mt-2 max-w-md text-sm text-black/60">
                We&apos;ll add it and let you know when it&apos;s live.
              </p>
            </div>
          ) : (
            <form data-testid="request-form" onSubmit={(event) => void handleSubmit(event)}>
              <h1 className="text-2xl font-bold text-black">
                Request a coffee or a roaster
              </h1>
              <p className="mt-2 mb-5 text-sm text-black/60">
                {mode === 'link'
                  ? "Paste a link to a coffee's product page, or a roaster's shop. We'll source it very soon."
                  : "Give us the coffee or roaster name and we'll source it very soon."}
              </p>

              <label
                htmlFor="request-input"
                className="mb-1.5 block text-sm font-bold text-wolisho-brown"
              >
                {mode === 'link' ? 'Link' : 'Name'}
              </label>
              <TextInput
                id="request-input"
                fullWidth
                type={mode === 'link' ? 'url' : 'text'}
                value={mode === 'link' ? link : name}
                onChange={mode === 'link' ? setLink : setName}
                placeholder={
                  mode === 'link'
                    ? 'Paste a link…'
                    : 'e.g. Poloco, or Colombia Monteblanco'
                }
                testId="request-input"
              />

              {error && (
                <p role="alert" data-testid="request-error" className="mt-2 text-sm text-wolisho-red">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="request-submit"
                className="mt-4 w-full rounded-lg bg-wolisho-red px-6 py-3 font-semibold text-white transition-colors hover:bg-wolisho-red/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Sending…' : 'Send request'}
              </button>

              <button
                type="button"
                onClick={() => setMode(mode === 'link' ? 'name' : 'link')}
                data-testid="request-toggle-mode"
                className="mt-3 block w-full text-center text-sm text-black/50 hover:text-wolisho-red"
              >
                {mode === 'link'
                  ? 'No link? Tell us the name instead'
                  : 'Have a link? Paste it instead'}
              </button>
            </form>
          )}
        </article>
      </div>
    </Layout>
  );
}
